/**
 * ConditionalQuestionControls Component
 *
 * Provides UI controls for SatisfactionSurvey conditional questions functionality.
 * Allows users to set rating thresholds for when questions should be displayed.
 */

import { Checkbox } from '@WebComponents/Checkbox';
import { TextInputFormField } from '@WebComponents/TextInputFormField';
import * as React from 'react';
import { ConditionalQuestionControlsProps } from '../types';

/**
 * Controls for managing conditional question display logic
 */
export const ConditionalQuestionControls: React.FC<ConditionalQuestionControlsProps> = ({
	field,
	fieldIndex,
	config,
	metadata,
	onConditionalToggle,
	onRatingThresholdChange,
}) => {
	// Calculate current state (must be before early return to follow hooks rules)
	const isConditional = metadata?.isConditional || false;
	const currentThreshold =
		metadata?.ratings?.[metadata.ratings.length - 1] || config.defaultRatings[config.defaultRatings.length - 1];

	// Handle checkbox change
	const handleCheckboxChange = React.useCallback(() => {
		onConditionalToggle(field.id, !isConditional);
	}, [field.id, isConditional, onConditionalToggle]);

	// Handle rating threshold change
	const handleThresholdChange = React.useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			const threshold = parseInt(e.target.value, 10);
			if (!isNaN(threshold) && threshold >= config.ratingRange[0] && threshold <= config.ratingRange[1]) {
				onRatingThresholdChange(field.id, threshold);
			}
		},
		[field.id, config.ratingRange, onRatingThresholdChange]
	);

	// Handle key down to prevent invalid characters
	const handleKeyDown = React.useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
		// Prevent typing of decimal points, negative signs, and plus signs
		if (e.key === '.' || e.key === '-' || e.key === '+') {
			e.preventDefault();
		}
	}, []);

	// Don't render if conditional questions are not enabled
	if (!config.enabled) {
		return null;
	}

	return (
		<div style={{ padding: '10px' }}>
			<Checkbox
				checked={isConditional}
				id={`edit-survey-ask-question-${fieldIndex}-checkbox`}
				onChange={handleCheckboxChange}
			>
				<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
					<span>Ask this question if the rating is</span>
					<TextInputFormField
						disabled={!isConditional}
						value={currentThreshold?.toString() || ''}
						type='number'
						min={config.ratingRange[0]}
						max={config.ratingRange[1]}
						style={{ width: '60px', display: 'inline-block' }}
						onKeyDown={handleKeyDown}
						onChange={handleThresholdChange}
					/>
					<span>star{(currentThreshold || 0) > 1 ? 's' : ''} or below</span>
				</div>
			</Checkbox>
		</div>
	);
};
